import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FiVideo, FiPlay, FiX } from 'react-icons/fi';
import { getYouTubeEmbedUrl } from '../../utils/imageUtils';

const GaleriaVideos = () => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [videoSelecionado, setVideoSelecionado] = useState(null);

  useEffect(() => {
    fetchVideos();
  }, []);

  const fetchVideos = async () => {
    try {
      const response = await axios.get('/api/site/galeria-videos');
      setVideos(response.data);
    } catch (error) {
      console.error('Erro ao carregar vídeos:', error);
    } finally {
      setLoading(false);
    }
  };

  const extrairIdDoYoutube = (url) => {
    if (!url) return null;
    
    // Múltiplos padrões para extrair o ID
    let videoId = null;
    
    // Padrão 1: youtube.com/shorts/VIDEO_ID (YouTube Shorts)
    const matchShorts = url.match(/youtube\.com\/shorts\/([^"&?\/\s]{11})/);
    if (matchShorts && matchShorts[1]) {
      videoId = matchShorts[1];
    }
    
    // Padrão 2: youtube.com/watch?v=VIDEO_ID
    if (!videoId) {
      const match1 = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^"&?\/\s]{11})/);
      if (match1 && match1[1]) {
        videoId = match1[1];
      }
    }
    
    // Padrão 3: youtube.com/v/VIDEO_ID
    if (!videoId) {
      const match2 = url.match(/youtube\.com\/v\/([^"&?\/\s]{11})/);
      if (match2 && match2[1]) {
        videoId = match2[1];
      }
    }
    
    // Padrão 4: regex genérica
    if (!videoId) {
      const match3 = url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/);
      if (match3 && match3[1]) {
        videoId = match3[1];
      }
    }
    
    return videoId;
  };

  return (
    <div className="bg-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-sky-600 to-sky-500 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-5xl font-bold mb-4">Galeria de Vídeos</h1>
          <p className="text-xl text-sky-100">Confira nossos vídeos e conheça nossos destinos</p>
        </div>
      </section>

      {/* Vídeos */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sky-600"></div>
            </div>
          ) : videos.length === 0 ? (
            <div className="text-center py-12">
              <FiVideo className="mx-auto text-gray-400 mb-4" size={64} />
              <p className="text-gray-600 text-lg">Nenhum vídeo disponível no momento.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {videos.map((video) => {
                const videoId = extrairIdDoYoutube(video.url);
                const thumbnailUrl = videoId ? `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg` : null;

                return (
                  <div
                    key={video.id}
                    className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow cursor-pointer"
                    onClick={() => setVideoSelecionado(video)}
                  >
                    {thumbnailUrl ? (
                      <div className="relative h-48 bg-gray-200">
                        <img
                          src={thumbnailUrl}
                          alt={video.titulo}
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                          <FiPlay className="text-white" size={48} />
                        </div>
                      </div>
                    ) : (
                      <div className="h-48 bg-gradient-to-br from-sky-100 to-sky-200 flex items-center justify-center">
                        <FiVideo className="text-sky-400" size={48} />
                      </div>
                    )}
                    <div className="p-6">
                      <h3 className="text-xl font-bold text-gray-900 mb-2">{video.titulo}</h3>
                      {video.descricao && (
                        <p className="text-gray-600 text-sm line-clamp-2">{video.descricao}</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Modal de Vídeo */}
      {videoSelecionado && (
        <div
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4"
          onClick={() => setVideoSelecionado(null)}
        >
          <div className="bg-white rounded-lg max-w-4xl w-full p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-bold text-gray-900">{videoSelecionado.titulo}</h2>
              <button
                onClick={() => setVideoSelecionado(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <FiX size={24} />
              </button>
            </div>
            <div className="aspect-video bg-black rounded-lg overflow-hidden relative">
              {(() => {
                const embedUrl = getYouTubeEmbedUrl(videoSelecionado.url);
                console.log('URL original:', videoSelecionado.url);
                console.log('URL embed:', embedUrl);
                
                if (!embedUrl) {
                  return (
                    <div className="w-full h-full flex items-center justify-center text-white">
                      <div className="text-center p-4">
                        <FiVideo className="mx-auto mb-2" size={48} />
                        <p className="text-lg font-semibold">Erro ao carregar vídeo</p>
                        <p className="text-sm text-gray-400 mt-2 break-all px-4">
                          URL: {videoSelecionado.url || 'Não informada'}
                        </p>
                        {videoSelecionado.url && (
                          <a
                            href={videoSelecionado.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sky-400 hover:text-sky-300 underline mt-4 inline-block"
                          >
                            Abrir no YouTube
                          </a>
                        )}
                      </div>
                    </div>
                  );
                }
                return (
                  <iframe
                    key={embedUrl}
                    src={embedUrl}
                    className="w-full h-full absolute inset-0"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                    title={videoSelecionado.titulo}
                    loading="lazy"
                  />
                );
              })()}
            </div>
            {videoSelecionado.descricao && (
              <p className="mt-4 text-gray-700">{videoSelecionado.descricao}</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default GaleriaVideos;
